import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, FileCheck, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileToBase64 } from "@/lib/api";

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "", country: "",
  linkedin: "", portfolio: "", cover_letter: "", years_experience: "",
  current_company: "", availability: "", salary_expectations: "",
};

export const ApplicationModal = ({ job, open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [resume, setResume] = useState(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = [".pdf", ".doc", ".docx"].some((x) => file.name.toLowerCase().endsWith(x));
    if (!ok) return toast.error("Please upload a PDF, DOC, or DOCX file.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Resume must be under 5MB.");
    setResume(file);
  };

  const reset = () => {
    setForm(EMPTY);
    setResume(null);
    setConsent(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please accept the consent checkbox.");
    setSubmitting(true);
    try {
      let resume_filename = "";
      let resume_base64 = "";
      if (resume) {
        resume_filename = resume.name;
        resume_base64 = await fileToBase64(resume);
      }
      await api.post(`/public/jobs/${job.id}/apply`, {
        ...form,
        email: form.email.trim().toLowerCase(),
        resume_filename,
        resume_base64,
        consent,
      });
      toast.success("Application submitted! Our team will be in touch.");
      reset();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!job) return null;

  const field = "bg-black/40 border-white/10 text-white rounded-xl h-10 focus:border-[#65FFB6]/50 text-sm";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-[#080B0E] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="application-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl">
            Apply — <span className="text-[#65FFB6]">{job.title}</span>
          </DialogTitle>
          <p className="text-xs font-mono text-slate-500 uppercase tracking-widest">{job.location}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="application-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1"><Label className="text-xs text-slate-300">First Name *</Label>
              <Input data-testid="app-first-name" required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={field} /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-300">Last Name *</Label>
              <Input data-testid="app-last-name" required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={field} /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-300">Email *</Label>
              <Input data-testid="app-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={field} /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-300">Phone</Label>
              <Input data-testid="app-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={field} /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-300">Country</Label>
              <Input data-testid="app-country" value={form.country} onChange={(e) => set("country", e.target.value)} className={field} /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-300">Years of Experience</Label>
              <Input data-testid="app-experience" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} className={field} /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-300">LinkedIn</Label>
              <Input data-testid="app-linkedin" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} className={field} /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-300">Portfolio</Label>
              <Input data-testid="app-portfolio" value={form.portfolio} onChange={(e) => set("portfolio", e.target.value)} className={field} /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-300">Current Company</Label>
              <Input data-testid="app-company" value={form.current_company} onChange={(e) => set("current_company", e.target.value)} className={field} /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-300">Availability</Label>
              <Input data-testid="app-availability" placeholder="e.g. 2 weeks notice" value={form.availability} onChange={(e) => set("availability", e.target.value)} className={field} /></div>
            <div className="space-y-1 sm:col-span-2"><Label className="text-xs text-slate-300">Salary Expectations</Label>
              <Input data-testid="app-salary" value={form.salary_expectations} onChange={(e) => set("salary_expectations", e.target.value)} className={field} /></div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-slate-300">Resume / CV * (PDF, DOC, DOCX · max 5MB)</Label>
            {resume ? (
              <div className="flex items-center justify-between bg-[#0B1F1A] border border-emerald-500/30 rounded-xl px-4 h-10" data-testid="app-resume-chip">
                <span className="flex items-center gap-2 text-xs font-mono text-[#65FFB6] truncate"><FileCheck className="w-4 h-4" />{resume.name}</span>
                <button type="button" onClick={() => setResume(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <label className="flex items-center gap-2 cursor-pointer bg-black/40 border border-dashed border-white/15 hover:border-[#65FFB6]/50 rounded-xl px-4 h-10 text-xs text-slate-400 hover:text-white transition-all" data-testid="app-resume-label">
                <Upload className="w-4 h-4 text-[#65FFB6]" /> Attach resume
                <input data-testid="app-resume-input" type="file" accept=".pdf,.doc,.docx" onChange={handleFile} className="hidden" />
              </label>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-slate-300">Cover Letter</Label>
            <Textarea data-testid="app-cover-letter" rows={4} value={form.cover_letter} onChange={(e) => set("cover_letter", e.target.value)} className="bg-black/40 border-white/10 text-white rounded-xl text-sm focus:border-[#65FFB6]/50" />
          </div>

          <label className="flex items-start gap-2.5 text-xs text-slate-400 cursor-pointer">
            <Checkbox data-testid="app-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-white/20 data-[state=checked]:bg-[#65FFB6] data-[state=checked]:text-black" />
            I consent to NovaCore Staffing storing and processing my data for recruitment purposes.
          </label>

          <Button data-testid="app-submit-btn" type="submit" disabled={submitting} className="w-full bg-[#65FFB6] hover:bg-[#A4FFD6] text-black font-semibold rounded-xl h-11 flex items-center justify-center gap-2">
            <Send className="w-4 h-4" /> {submitting ? "Submitting..." : "Submit Application"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
